import type { Schemas } from '@/lib/api/client';
import { eventStatusLabel } from './eventStatus';
import { formatNumber } from '@/lib/format/format';

export type Curation = 'cancel' | 'reinstate';

type Facts = Pick<Schemas['EventFactsDto'], 'interestCount'>;

/** Who hears about a change: the runners who marked interest, or nobody yet. Unknown while loading. */
export function told(interest: number | undefined, what?: string, future = false): string {
  const tail = what ? ` ${what}` : '';
  if (interest === undefined)
    return `the runners who marked interest ${future ? 'will be' : 'are'} told${tail}`;
  if (interest === 0) return 'nobody has marked interest yet';
  const runners = `${formatNumber(interest)} runner${interest === 1 ? '' : 's'}`;
  const verb = future ? 'will be' : interest === 1 ? 'is' : 'are';
  return `the ${runners} who marked interest ${verb} told${tail}`;
}

export const cancelText = (interest: number | undefined): string =>
  `It stays in the app with a Cancelled badge, and ${told(interest)}.`;

export const reinstateText = (interest: number | undefined): string =>
  `The Cancelled badge goes, and ${told(interest, 'it is back on')}.`;

const WORDS = {
  cancel: {
    title: ['Mark “', '” cancelled?'],
    itemsLabel: eventStatusLabel('cancelled'),
    confirmLabel: 'Mark cancelled',
    tone: 'critical',
    text: cancelText,
  },
  reinstate: {
    title: ['Reinstate “', '”?'],
    itemsLabel: 'Back on',
    confirmLabel: 'Reinstate',
    tone: 'primary',
    text: reinstateText,
  },
} as const;

/** The confirm for cancelling or reinstating an event, reading its facts once they've loaded. */
export function curateConfirm(curation: Curation, facts?: Facts) {
  const { text, ...words } = WORDS[curation];
  return { ...words, text: text(facts?.interestCount) };
}
